import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { MatchPreviewComponent } from '../match-preview/match-preview.component';
import { SportIconComponent } from '../sport-icon/sport-icon.component';
import { getSportPresentation } from '../sportsbook/config/sportsbook-sports.config';
import { SelectionToggleRequest, TournamentGroupViewModel } from '../sportsbook/sportsbook.models';

const MAX_DISPLAYED_MATCH_COUNT = 99;

@Component({
  selector: 'app-tournament-group',
  standalone: true,
  imports: [TranslateModule, MatchPreviewComponent, SportIconComponent],
  templateUrl: './tournament-group.component.html',
  styleUrl: './tournament-group.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TournamentGroupComponent {
  readonly group = input.required<TournamentGroupViewModel>();
  readonly mobile = input(false);
  readonly selectedQuickGroupId = input<string | null>(null);

  readonly toggleExpanded = output<number>();
  readonly selectionToggle = output<SelectionToggleRequest>();
  readonly openMatchDetail = output<number>();
  readonly selectedQuickGroupChange = output<string>();

  protected readonly sportIconName = computed(() => getSportPresentation(this.group().sportId).iconName);
  protected readonly displayedMatchCount = computed(() => Math.min(this.group().matchCount, MAX_DISPLAYED_MATCH_COUNT));
  protected readonly selectedQuickGroup = computed(() => {
    const groups = this.group().quickSelectionGroups;
    return groups.find(group => group.id === this.selectedQuickGroupId()) ?? groups[0];
  });
  protected readonly visibleQuickGroups = computed(() => {
    if (!this.mobile()) {
      return this.group().quickSelectionGroups;
    }

    const selectedGroup = this.selectedQuickGroup();
    return selectedGroup ? [selectedGroup] : [];
  });
  protected readonly visibleQuickGroupIds = computed(() => this.visibleQuickGroups().map(group => group.id));
  protected readonly visibleQuickColumnCount = computed(() => {
    return this.visibleQuickGroups().reduce((count, group) => count + group.headerLabels.length, 0);
  });

  protected readonly hideQuickHeaderLabels = computed(() => {
    const matches = this.group().matches;
    return matches.length > 0 && matches.every(match => match.isOutright);
  });

  protected selectQuickGroup(event: Event): void {
    const quickGroupId = (event.target as HTMLSelectElement).value;
    if (this.group().quickSelectionGroups.some(group => group.id === quickGroupId)) {
      this.selectedQuickGroupChange.emit(quickGroupId);
    }
  }
}
